/**
 * Tag Manager Service - handles tag operations
 * (insert, parse, highlight, remove)
 */

import { inject, Injectable } from '@angular/core'
import {
  CSS_CLASSES,
  CSS_SELECTORS,
  CSS_STYLE_VALUES,
  DEBUG_MESSAGES,
  DEBUG_PREVIEW_LENGTHS,
  DEFAULT_VALUES,
  DOM_ELEMENT_NAMES,
  GOOGLE_DOCS_ARTIFACTS,
  HTML_ATTRIBUTES,
  HTML_VALUES,
  PRESERVED_FORMATTING_ELEMENTS,
  TAG_EDITOR_COLORS,
  TAG_EDITOR_DELAY,
  TAG_EDITOR_STRINGS,
  TAG_EDITOR_THRESHOLDS,
  TAG_EDITOR_VALUES,
} from '../tag-editor.constants'
import { InsertedTagCustom, PastedTagDataCustom } from '../tag-editor.model'
import { generateWCAGCompliantTagColors } from '../../../../../shared/helpers/color.utils'
import { DebugLoggingService } from './debug-logging.service'

@Injectable({ providedIn: 'root' })
export class TagManagerService {
  private readonly debugLoggingService = inject(DebugLoggingService)

  /**
   * Generates a unique tag ID using crypto.randomUUID()
   * Ensures each tag has a unique identifier for tracking and manipulation
   */
  generateTagId(): string {
    return `${TAG_EDITOR_STRINGS.TAG_PREFIX}${crypto.randomUUID()}`
  }

  /**
   * Creates a styled HTML span element for tag representation
   * Applies WCAG-compliant colors and proper accessibility attributes
   * Sets up contenteditable for inline editing capability
   */
  createTagElement(
    id: string,
    label: string,
    content: string | DocumentFragment | Node | null,
    color: string,
    category?: string,
    categoryId?: number,
  ): HTMLElement {
    const span = document.createElement(DOM_ELEMENT_NAMES.SPAN)

    // Set attributes
    span.setAttribute(HTML_ATTRIBUTES.DATA_TAG_ID, id)
    span.setAttribute(HTML_ATTRIBUTES.ID, id)
    span.setAttribute(HTML_ATTRIBUTES.DATA_LABEL, label)
    span.setAttribute(HTML_ATTRIBUTES.DATA_COLOR, color)
    span.setAttribute(HTML_ATTRIBUTES.CLASS, CSS_CLASSES.TAG_ANCHOR)
    span.setAttribute(HTML_ATTRIBUTES.CONTENTEDITABLE, HTML_VALUES.CONTENTEDITABLE_TRUE)
    span.setAttribute(HTML_ATTRIBUTES.TITLE, `${HTML_VALUES.TAG_TITLE_PREFIX}${label}`)

    if (category) {
      span.setAttribute(HTML_ATTRIBUTES.DATA_CATEGORY, category)
    }

    if (categoryId !== undefined) {
      span.setAttribute(HTML_ATTRIBUTES.DATA_CATEGORY_ID, categoryId.toString())
    }

    // Set content - preserve formatting if DocumentFragment or Node, otherwise use textContent
    if (content === null) {
      // Leave empty
    } else if (content instanceof DocumentFragment) {
      // Append all nodes from fragment to preserve formatting
      while (content.firstChild) {
        span.appendChild(content.firstChild)
      }
    } else if (content instanceof Node) {
      // Append node directly to preserve formatting
      span.appendChild(content)
    } else {
      // String - use textContent for backward compatibility
      span.textContent = content
    }

    // Apply styles
    const rgb = this.hexToRgb(color)
    if (rgb) {
      span.style.color = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`
      span.style.textDecoration = CSS_STYLE_VALUES.TAG_TEXT_DECORATION_NONE
      span.style.backgroundColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${TAG_EDITOR_COLORS.TAG_BACKGROUND_ALPHA})`
      span.style.padding = CSS_STYLE_VALUES.TAG_PADDING
      span.style.borderRadius = CSS_STYLE_VALUES.TAG_BORDER_RADIUS
      span.style.fontWeight = CSS_STYLE_VALUES.TAG_FONT_WEIGHT
      span.style.cursor = CSS_STYLE_VALUES.TAG_CURSOR
      span.style.border = `${CSS_STYLE_VALUES.TAG_BORDER_WIDTH} rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`
    }

    return span
  }

  /**
   * Parse tags from HTML content
   * Only elements with data-tag-id are considered valid tags
   * Each tag is added only once (by ID) to prevent duplicates
   */
  parseTagsFromContent(editorElement: HTMLElement): ReadonlyArray<InsertedTagCustom> {
    // Only select elements with data-tag-id attribute (primary identifier for tags)
    const tagElements = editorElement.querySelectorAll(
      `${DOM_ELEMENT_NAMES.SPAN}[${HTML_ATTRIBUTES.DATA_TAG_ID}], u[${HTML_ATTRIBUTES.DATA_TAG_ID}]`,
    )

    this.debugLoggingService.logWithService('TagManagerService', 'parseTagsFromContent started:', {
      totalElementsFound: tagElements.length,
    })

    const tags: InsertedTagCustom[] = []
    const seenIds = new Set<string>()

    tagElements.forEach((element, index) => {
      const id = element.getAttribute(HTML_ATTRIBUTES.DATA_TAG_ID) || element.getAttribute(HTML_ATTRIBUTES.ID) || ''

      // Only process elements that have data-tag-id (required attribute for tags)
      if (!id) {
        return
      }

      // Skip if this tag ID was already processed (prevent duplicates)
      if (seenIds.has(id)) {
        return
      }

      // Skip if element is nested inside another tag element (only process top-level tags)
      let parent = element.parentElement
      while (parent && parent !== editorElement) {
        if (parent.hasAttribute(HTML_ATTRIBUTES.DATA_TAG_ID)) {
          return
        }
        parent = parent.parentElement
      }

      seenIds.add(id)

      const label = element.getAttribute(HTML_ATTRIBUTES.DATA_LABEL) || ''
      const color = element.getAttribute(HTML_ATTRIBUTES.DATA_COLOR) || ''

      let text = element.textContent || ''

      // If text is very short and element has parent, try to get text from parent
      if (text.length < TAG_EDITOR_VALUES.MIN_TAG_TEXT_LENGTH && element.parentElement) {
        const parentText = element.parentElement.textContent || ''
        if (parentText.length > text.length) {
          text = parentText
        }
      }

      // Require at least id and label (or text as fallback for label)
      if (id && (label || text)) {
        const wcagColors = generateWCAGCompliantTagColors(label || text)

        tags.push({
          id,
          label: label || text,
          text,
          color: color || wcagColors.foreground,
          backgroundColor: wcagColors.background,
          backgroundColorHover: wcagColors.backgroundHover,
          data: undefined,
        })

        this.debugLoggingService.logWithService(
          'TagManagerService',
          `parseTagsFromContent: Added tag ${tags.length}:`,
          {
            id,
            label: label || text,
            index,
          },
        )
      } else {
        this.debugLoggingService.logWithService(
          'TagManagerService',
          `parseTagsFromContent: Skipped element ${index}:`,
          {
            reason: !id ? 'missing-id' : 'missing-label-and-text',
            id,
            label,
            text: text.substring(0, DEBUG_PREVIEW_LENGTHS.TEXT_MISMATCH_PREVIEW),
          },
        )
      }
    })

    this.debugLoggingService.logWithService('TagManagerService', 'parseTagsFromContent completed:', {
      totalElementsFound: tagElements.length,
      tagsParsed: tags.length,
    })

    return tags
  }

  /**
   * Assigns unique IDs to all tags in HTML before pasting
   * Preserves all tag attributes (label, color, category, etc.)
   * Returns modified HTML with new unique IDs for each tag
   */
  assignUniqueIdsToTagsInHTML(html: string, generateTagId: () => string): string {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    // Find all elements with data-tag-id
    const tagElements = doc.querySelectorAll(
      `${DOM_ELEMENT_NAMES.SPAN}[${HTML_ATTRIBUTES.DATA_TAG_ID}], u[${HTML_ATTRIBUTES.DATA_TAG_ID}]`,
    )

    // Assign new unique ID to each tag while preserving all other attributes
    tagElements.forEach((element) => {
      const htmlElement = element as HTMLElement

      // Preserve existing attributes
      const label = htmlElement.getAttribute(HTML_ATTRIBUTES.DATA_LABEL) || ''
      const color = htmlElement.getAttribute(HTML_ATTRIBUTES.DATA_COLOR) || ''
      const category = htmlElement.getAttribute(HTML_ATTRIBUTES.DATA_CATEGORY) || ''
      const categoryId = htmlElement.getAttribute(HTML_ATTRIBUTES.DATA_CATEGORY_ID) || ''
      const style = htmlElement.getAttribute(HTML_ATTRIBUTES.STYLE) || ''
      const className = htmlElement.getAttribute(HTML_ATTRIBUTES.CLASS) || ''
      const title = htmlElement.getAttribute(HTML_ATTRIBUTES.TITLE) || ''
      // Generate new unique ID
      const newTagId = generateTagId()

      // Set new ID
      htmlElement.setAttribute(HTML_ATTRIBUTES.DATA_TAG_ID, newTagId)
      htmlElement.setAttribute(HTML_ATTRIBUTES.ID, newTagId)

      // Restore all preserved attributes
      if (label) {
        htmlElement.setAttribute(HTML_ATTRIBUTES.DATA_LABEL, label)
      }
      if (color) {
        htmlElement.setAttribute(HTML_ATTRIBUTES.DATA_COLOR, color)
      }
      if (category) {
        htmlElement.setAttribute(HTML_ATTRIBUTES.DATA_CATEGORY, category)
      }
      if (categoryId) {
        htmlElement.setAttribute(HTML_ATTRIBUTES.DATA_CATEGORY_ID, categoryId)
      }
      if (style) {
        htmlElement.setAttribute(HTML_ATTRIBUTES.STYLE, style)
      }
      // Ensure CSS class is set (required for styling)
      if (!className || !className.includes(CSS_CLASSES.TAG_ANCHOR)) {
        htmlElement.setAttribute(HTML_ATTRIBUTES.CLASS, CSS_CLASSES.TAG_ANCHOR)
      } else {
        htmlElement.setAttribute(HTML_ATTRIBUTES.CLASS, className)
      }
      if (title) {
        htmlElement.setAttribute(HTML_ATTRIBUTES.TITLE, title)
      }
      // Always set contenteditable to false for pasted tags (will be enabled during inline editing)
      htmlElement.setAttribute(HTML_ATTRIBUTES.CONTENTEDITABLE, HTML_VALUES.CONTENTEDITABLE_FALSE)
    })

    // Serialize back to HTML
    const tempDiv = document.createElement(DOM_ELEMENT_NAMES.DIV)
    const bodyNodes = Array.from(doc.body.childNodes)
    bodyNodes.forEach((node) => {
      tempDiv.appendChild(node.cloneNode(true))
    })

    return tempDiv.innerHTML
  }

  /**
   * Extract tag data from HTML for copy-paste preservation
   */
  extractTagDataFromHTML(html: string): ReadonlyArray<PastedTagDataCustom> {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    // Only select elements with data-tag-id (primary identifier)
    // Elements with only data-label are not valid tags for extraction
    const tagElements = doc.querySelectorAll(
      `${DOM_ELEMENT_NAMES.SPAN}[${HTML_ATTRIBUTES.DATA_TAG_ID}], u[${HTML_ATTRIBUTES.DATA_TAG_ID}]`,
    )

    const tags: PastedTagDataCustom[] = []
    const seenIds = new Set<string>()

    tagElements.forEach((element, _index) => {
      const id = element.getAttribute(HTML_ATTRIBUTES.DATA_TAG_ID) || element.getAttribute(HTML_ATTRIBUTES.ID) || ''

      // Only process elements that have data-tag-id (required attribute)
      if (!id) {
        return
      }

      // Skip duplicates by ID
      if (seenIds.has(id)) {
        return
      }

      // Skip if element is nested inside another tag element
      let parent = element.parentElement
      while (parent && parent !== doc.body) {
        if (parent.hasAttribute(HTML_ATTRIBUTES.DATA_TAG_ID)) {
          return
        }
        parent = parent.parentElement
      }

      seenIds.add(id)

      const label = element.getAttribute(HTML_ATTRIBUTES.DATA_LABEL) || ''
      const color = element.getAttribute(HTML_ATTRIBUTES.DATA_COLOR) || ''
      const text = element.textContent || ''
      const category = element.getAttribute(HTML_ATTRIBUTES.DATA_CATEGORY) || ''
      const categoryId = element.getAttribute(HTML_ATTRIBUTES.DATA_CATEGORY_ID) || ''
      const style = element.getAttribute(HTML_ATTRIBUTES.STYLE) || ''

      // Require at least id and label (or text as fallback)
      if (id && (label || text)) {
        tags.push({
          id,
          label: label || text,
          color: color || DEFAULT_VALUES.BLACK_COLOR,
          text,
          category: category || undefined,
          categoryId: categoryId || undefined,
          style: style || undefined,
        })
      }
    })

    this.debugLoggingService.logWithService('TagManagerService', 'extractTagDataFromHTML completed:', {
      totalElementsFound: tagElements.length,
      tagsExtracted: tags.length,
    })

    return tags
  }

  /**
   * Restore tag attributes after paste (when browser strips them)
   */
  restoreTagAttributes(editorElement: HTMLElement, extractedTags: ReadonlyArray<PastedTagDataCustom>): number {
    if (extractedTags.length === 0) {
      this.debugLoggingService.logWithService('TagManagerService', 'restoreTagAttributes: No extracted tags to restore')
      return 0
    }

    this.debugLoggingService.logWithService('TagManagerService', 'restoreTagAttributes started:', {
      extractedTagsCount: extractedTags.length,
    })

    // First, try to find elements by their original data-tag-id
    let restoredById = 0
    const restoredElements = new Set<HTMLElement>()

    extractedTags.forEach((tag) => {
      // Find ALL elements with this ID (can be multiple)
      const elements = Array.from(
        editorElement.querySelectorAll(`[${HTML_ATTRIBUTES.DATA_TAG_ID}="${tag.id}"]`),
      ) as HTMLElement[]

      elements.forEach((element) => {
        if (restoredElements.has(element)) {
          return
        }

        // Element already has the correct ID, just ensure attributes are set
        const newTagId: string = this.generateTagId()
        this.applyTagAttributesToElement(
          element,
          newTagId,
          tag.label,
          tag.color || DEFAULT_VALUES.BLACK_COLOR,
          tag.category,
          tag.categoryId,
        )
        if (tag.style) {
          element.setAttribute(HTML_ATTRIBUTES.STYLE, tag.style)
        }

        restoredElements.add(element)
        restoredById++
      })
    })

    // Then, for remaining tags, try text-based matching
    let restoredByText = 0
    const potentialTags = editorElement.querySelectorAll(
      `${DOM_ELEMENT_NAMES.SPAN}:not([${HTML_ATTRIBUTES.DATA_TAG_ID}]), u:not([${HTML_ATTRIBUTES.DATA_TAG_ID}])`,
    )

    extractedTags.forEach((tag, _tagIndex) => {
      // Skip if already restored
      const alreadyRestored = Array.from(restoredElements).some((el) => {
        const elText = el.textContent?.trim() || ''
        return this.calculateTextSimilarity(elText, tag.text.trim()) > TAG_EDITOR_THRESHOLDS.TEXT_SIMILARITY_THRESHOLD
      })

      if (alreadyRestored) {
        return
      }

      potentialTags.forEach((element) => {
        const htmlElement = element as HTMLElement

        if (restoredElements.has(htmlElement) || element.hasAttribute(HTML_ATTRIBUTES.DATA_TAG_ID)) {
          return
        }

        const elementText = htmlElement.textContent?.trim() || ''
        const tagText = tag.text.trim()
        const similarity: number = this.calculateTextSimilarity(elementText, tagText)

        if (similarity > TAG_EDITOR_THRESHOLDS.TEXT_SIMILARITY_THRESHOLD) {
          // Generate new unique ID to avoid conflicts with existing tags
          const newTagId: string = this.generateTagId()
          this.applyTagAttributesToElement(
            htmlElement,
            newTagId,
            tag.label,
            tag.color || DEFAULT_VALUES.BLACK_COLOR,
            tag.category,
            tag.categoryId,
          )
          if (tag.style) {
            htmlElement.setAttribute(HTML_ATTRIBUTES.STYLE, tag.style)
          }

          restoredElements.add(htmlElement)
          restoredByText++
        }
      })
    })

    const totalRestored = restoredById + restoredByText
    this.debugLoggingService.logWithService('TagManagerService', 'restoreTagAttributes completed:', {
      extractedTagsCount: extractedTags.length,
      restoredById,
      restoredByText,
      totalRestored,
    })

    return totalRestored
  }

  /**
   * Find tag element by ID
   */
  findTagById(editorElement: HTMLElement, tagId: string): HTMLElement | null {
    return editorElement.querySelector(`[${HTML_ATTRIBUTES.DATA_TAG_ID}="${tagId}"]`)
  }

  /**
   * Highlight tag with animation
   */
  highlightTag(tagElement: HTMLElement): void {
    // Add initial highlight class
    tagElement.classList.add(CSS_CLASSES.TAG_HIGHLIGHT)

    // Add phase 2 class after delay
    setTimeout(() => {
      tagElement.classList.add(CSS_CLASSES.TAG_HIGHLIGHT_PHASE2)
    }, TAG_EDITOR_DELAY.HIGHLIGHT_ANIMATION_MS)

    // Remove all highlight classes after completion
    setTimeout(() => {
      tagElement.classList.remove(CSS_CLASSES.TAG_HIGHLIGHT, CSS_CLASSES.TAG_HIGHLIGHT_PHASE2)
    }, TAG_EDITOR_DELAY.HIGHLIGHT_COMPLETE_MS)

    // Scroll tag into view
    tagElement.scrollIntoView({ behavior: CSS_STYLE_VALUES.SCROLL_BEHAVIOR, block: CSS_STYLE_VALUES.SCROLL_BLOCK })
  }

  /**
   * Remove tag element from editor
   */
  removeTagElement(tagElement: HTMLElement): void {
    // Replace tag with its text content
    const text = tagElement.textContent || ''
    const textNode = document.createTextNode(text)
    tagElement.parentNode?.replaceChild(textNode, tagElement)
  }

  /**
   * Convert hex color to RGB object
   */
  private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    // Remove # if present
    const cleanHex = hex.replace('#', '')

    if (cleanHex.length !== 6) {
      return null
    }

    const r = parseInt(cleanHex.substring(0, 2), 16)
    const g = parseInt(cleanHex.substring(2, 4), 16)
    const b = parseInt(cleanHex.substring(4, 6), 16)

    return { r, g, b }
  }

  /**
   * Reset all tags to non-editable state
   */
  resetTagsToNonEditable(editorElement: HTMLElement): void {
    const tags = editorElement.querySelectorAll(CSS_SELECTORS.TAG_BY_ID)
    tags.forEach((tag) => {
      tag.setAttribute(HTML_ATTRIBUTES.CONTENTEDITABLE, HTML_VALUES.CONTENTEDITABLE_FALSE)
      tag.classList.remove(CSS_CLASSES.TAG_EDITING)
    })
  }

  /**
   * Moves cursor outside the tag element (after the tag)
   */
  moveCursorOutsideTag(tagElement: HTMLElement): void {
    const selection = window.getSelection()
    if (!selection) {
      return
    }

    // Insert a space directly after the tag element using DOM manipulation
    const spaceTextNode = document.createTextNode(' ')
    tagElement.parentNode?.insertBefore(spaceTextNode, tagElement.nextSibling)

    // Create new range after the inserted space
    const range = document.createRange()
    range.setStartAfter(spaceTextNode)
    range.collapse(true)
    selection.removeAllRanges()
    selection.addRange(range)

    this.debugLoggingService.log(DEBUG_MESSAGES.CURSOR_MOVED_OUTSIDE)
  }

  /**
   * Moves cursor before the tag element
   */
  moveCursorBeforeTag(tagElement: HTMLElement): void {
    const selection = window.getSelection()
    if (!selection) {
      return
    }

    // Insert a space directly before the tag element using DOM manipulation
    const spaceTextNode = document.createTextNode(' ')
    tagElement.parentNode?.insertBefore(spaceTextNode, tagElement)

    // Create new range after the inserted space
    const range = document.createRange()
    range.setStartAfter(spaceTextNode)
    range.collapse(true)
    selection.removeAllRanges()
    selection.addRange(range)

    this.debugLoggingService.log(DEBUG_MESSAGES.CURSOR_MOVED_BEFORE_TAG)
  }

  /**
   * Normalizes tag structure to ensure consistency
   * Removes Google Docs artifacts, ensures proper attributes, and consistent structure
   * This is critical for inline editing to work reliably on all tags
   */
  normalizeTagStructure(editorElement: HTMLElement): number {
    const tagElements = editorElement.querySelectorAll(
      `${DOM_ELEMENT_NAMES.SPAN}[${HTML_ATTRIBUTES.DATA_TAG_ID}], u[${HTML_ATTRIBUTES.DATA_TAG_ID}]`,
    )

    this.debugLoggingService.logWithService('TagManagerService', 'normalizeTagStructure started:', {
      totalTags: tagElements.length,
    })

    let normalizedCount = 0

    tagElements.forEach((element, index) => {
      const htmlElement = element as HTMLElement
      const tagId = htmlElement.getAttribute(HTML_ATTRIBUTES.DATA_TAG_ID)

      if (!tagId) {
        return
      }

      let wasNormalized = false

      // Remove Google Docs artifacts
      const currentId = htmlElement.getAttribute(HTML_ATTRIBUTES.ID)
      if (currentId && (currentId.startsWith(GOOGLE_DOCS_ARTIFACTS.ID_PREFIX) || currentId !== tagId)) {
        htmlElement.removeAttribute(HTML_ATTRIBUTES.ID)
        htmlElement.setAttribute(HTML_ATTRIBUTES.ID, tagId)
        wasNormalized = true
      }

      // Remove empty style attributes
      const styleAttr = htmlElement.getAttribute(HTML_ATTRIBUTES.STYLE)
      if (
        styleAttr &&
        (styleAttr.trim() === TAG_EDITOR_STRINGS.EMPTY_STYLE ||
          styleAttr.trim() === TAG_EDITOR_STRINGS.EMPTY_STYLE_SEMICOLON)
      ) {
        htmlElement.removeAttribute(HTML_ATTRIBUTES.STYLE)
        wasNormalized = true
      }

      // Ensure contenteditable is false by default (will be enabled during inline editing)
      const currentContentEditable = htmlElement.getAttribute(HTML_ATTRIBUTES.CONTENTEDITABLE)
      if (currentContentEditable !== HTML_VALUES.CONTENTEDITABLE_FALSE) {
        htmlElement.setAttribute(HTML_ATTRIBUTES.CONTENTEDITABLE, HTML_VALUES.CONTENTEDITABLE_FALSE)
        wasNormalized = true
      }

      // Ensure CSS class is present
      if (!htmlElement.classList.contains(CSS_CLASSES.TAG_ANCHOR)) {
        htmlElement.classList.add(CSS_CLASSES.TAG_ANCHOR)
        wasNormalized = true
      }

      // Remove nested elements inside tag, but preserve formatting, list, and structural elements
      const children = Array.from(htmlElement.childNodes)
      let hasNestedElements = false
      children.forEach((child) => {
        if (child.nodeType === Node.ELEMENT_NODE) {
          const childElement = child as HTMLElement
          // Skip if nested element is a tag itself (shouldn't happen, but defensive)
          if (childElement.hasAttribute(HTML_ATTRIBUTES.DATA_TAG_ID)) {
            return
          }

          // Preserve elements that are in the whitelist (formatting, lists, structural)
          // tagName returns uppercase in HTML, so compare with uppercase whitelist
          const tagName = childElement.tagName.toUpperCase()
          if (PRESERVED_FORMATTING_ELEMENTS.has(tagName)) {
            // Keep preserved elements - they maintain user's formatting and structure
            return
          }

          // Replace other nested elements with their text content
          const textContent = childElement.textContent || ''
          const textNode = document.createTextNode(textContent)
          htmlElement.replaceChild(textNode, child)
          hasNestedElements = true
          wasNormalized = true
        }
      })

      // Ensure ID attribute matches data-tag-id (only if not already set correctly above)
      const finalId = htmlElement.getAttribute(HTML_ATTRIBUTES.ID)
      if (finalId !== tagId) {
        htmlElement.setAttribute(HTML_ATTRIBUTES.ID, tagId)
        wasNormalized = true
      }

      // Ensure required attributes are present
      const label = htmlElement.getAttribute(HTML_ATTRIBUTES.DATA_LABEL)
      if (!label && htmlElement.textContent) {
        htmlElement.setAttribute(HTML_ATTRIBUTES.DATA_LABEL, htmlElement.textContent.trim())
        wasNormalized = true
      }

      const color = htmlElement.getAttribute(HTML_ATTRIBUTES.DATA_COLOR)
      if (!color) {
        const defaultColor = TAG_EDITOR_COLORS.DEFAULT_TAG_COLOR
        htmlElement.setAttribute(HTML_ATTRIBUTES.DATA_COLOR, defaultColor)
        wasNormalized = true
      }

      // Ensure title attribute for accessibility
      const title = htmlElement.getAttribute(HTML_ATTRIBUTES.TITLE)
      const expectedTitle = `${TAG_EDITOR_STRINGS.TAG_TITLE_PREFIX}${label || htmlElement.textContent?.trim() || ''}`
      if (title !== expectedTitle) {
        htmlElement.setAttribute(HTML_ATTRIBUTES.TITLE, expectedTitle)
        wasNormalized = true
      }

      // Normalize tag styles: remove underline, add background highlight
      const tagColor = htmlElement.getAttribute(HTML_ATTRIBUTES.DATA_COLOR) || TAG_EDITOR_COLORS.DEFAULT_TAG_COLOR
      const rgb = this.hexToRgb(tagColor)
      if (rgb) {
        htmlElement.style.textDecoration = CSS_STYLE_VALUES.TAG_TEXT_DECORATION_NONE
        htmlElement.style.backgroundColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${TAG_EDITOR_COLORS.TAG_BACKGROUND_ALPHA})`
        htmlElement.style.color = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`
        htmlElement.style.border = `${CSS_STYLE_VALUES.TAG_BORDER_WIDTH} rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`
        htmlElement.style.padding = CSS_STYLE_VALUES.TAG_PADDING
        htmlElement.style.borderRadius = CSS_STYLE_VALUES.TAG_BORDER_RADIUS
        htmlElement.style.fontWeight = CSS_STYLE_VALUES.TAG_FONT_WEIGHT
        htmlElement.style.cursor = CSS_STYLE_VALUES.TAG_CURSOR
        wasNormalized = true
      }

      if (wasNormalized) {
        normalizedCount++
        this.debugLoggingService.logWithService(
          'TagManagerService',
          `normalizeTagStructure: Normalized tag ${index + 1}:`,
          {
            tagId,
            label: htmlElement.getAttribute(HTML_ATTRIBUTES.DATA_LABEL),
            hadNestedElements: hasNestedElements,
          },
        )
      }
    })

    this.debugLoggingService.logWithService('TagManagerService', 'normalizeTagStructure completed:', {
      totalTags: tagElements.length,
      normalizedCount,
    })

    return normalizedCount
  }

  /**
   * Applies tag attributes to an HTML element
   */
  public applyTagAttributesToElement(
    element: HTMLElement,
    tagId: string,
    label: string,
    color: string,
    categoryName?: string,
    categoryId?: string,
  ): void {
    element.setAttribute(HTML_ATTRIBUTES.DATA_TAG_ID, tagId)
    element.setAttribute(HTML_ATTRIBUTES.ID, tagId)
    element.setAttribute(HTML_ATTRIBUTES.DATA_LABEL, label)
    element.setAttribute(HTML_ATTRIBUTES.DATA_COLOR, color)
    element.setAttribute(HTML_ATTRIBUTES.CLASS, CSS_CLASSES.TAG_ANCHOR)
    element.setAttribute(HTML_ATTRIBUTES.CONTENTEDITABLE, HTML_VALUES.CONTENTEDITABLE_FALSE)
    element.setAttribute(HTML_ATTRIBUTES.TITLE, `${TAG_EDITOR_STRINGS.TAG_TITLE_PREFIX}${label}`)

    if (categoryName) {
      element.setAttribute(HTML_ATTRIBUTES.DATA_CATEGORY, categoryName)
    }
    if (categoryId) {
      element.setAttribute(HTML_ATTRIBUTES.DATA_CATEGORY_ID, categoryId)
    }
  }

  /**
   * Calculate text similarity for matching pasted tags
   */
  public calculateTextSimilarity(text1: string, text2: string): number {
    if (text1 === text2) {
      return 1
    }
    if (!text1 || !text2) {
      return 0
    }

    const longer: string = text1.length > text2.length ? text1 : text2
    const shorter: string = text1.length > text2.length ? text2 : text1

    if (longer.length === 0) {
      return 1
    }

    const editDistance: number = this.levenshteinDistance(longer, shorter)
    return (longer.length - editDistance) / longer.length
  }

  /**
   * Calculate Levenshtein distance between two strings
   */
  private levenshteinDistance(str1: string, str2: string): number {
    const matrix: number[][] = []

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i]
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1]
        } else {
          matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j] + 1)
        }
      }
    }

    return matrix[str2.length][str1.length]
  }
}
